import { debugTracking } from '$lib/debug';
import { isTrackingAllowed } from '$lib/global/consent/consentGate';
import { shouldTrack } from '$lib/global/tracking/helper';

import omit from 'lodash/omit';

import { dispatchIdentify, dispatchPage, dispatchTrack } from './firstPartyDispatch';
import { integrationsWithIdsInEvents, integrationsWithOutIdsInEvents } from './integrations';
import { getTrackingService } from './TrackingServiceInstance';

export const trackQuantityEvent = (event: string, properties?: Record<string, unknown>) => {
    track(event, properties, {
        integrations: integrationsWithOutIdsInEvents,
    });
};

export const trackUniqueEvent = (event: string, properties: Record<string, unknown>) => {
    track(event, properties, {
        integrations: integrationsWithIdsInEvents,
    });
};

/**
 * A visitor without consent is measured, but only by us.
 *
 * The event still goes out — straight to the funnel's own origin, skipping analytics.js and
 * therefore every third-party integration. See `firstPartyDispatch`.
 */
const isFirstPartyOnly = (): boolean => !isTrackingAllowed();

/**
 * Whether the Perspective integration would have received this event.
 *
 * Segment applies the per-integration overrides for us on the consented path, and
 * `trackUniqueEvent` uses that to disable `Perspective` explicitly: the `<Event>: <id>`
 * variants exist to give the third-party pixels a per-id event name, and our own backend has
 * never accepted them — it answers 400. `firstPartyDispatch` posts straight to that backend,
 * so it has to apply the same routing itself. Changing the transport must not change which
 * destinations an event reaches.
 *
 * Absent means enabled, matching Segment's treatment of a destination the caller did not
 * mention.
 */
const goesToPerspective = (override: { integrations?: Record<string, boolean> }): boolean =>
    override.integrations?.Perspective !== false;

export const track = (
    event: string,
    properties: Record<string, unknown> | undefined,
    override: { integrations?: Record<string, boolean> } = {},
) => {
    debugTracking('-> V4 track request:', !!window.analytics, event, properties, override);

    if (!shouldTrack()) {
        return;
    }

    const { context } = generateContext();

    if (isFirstPartyOnly()) {
        if (goesToPerspective(override)) {
            dispatchTrack(event, properties, context);
        }

        return;
    }

    window.analytics?.track(event, properties, {
        ...override,
        context,
    });
};

export const page = (pageName: string, properties: Record<string, unknown>) => {
    debugTracking('-> V4 page request:', !!window.analytics, pageName, properties);

    if (!shouldTrack()) {
        return;
    }

    const { context } = generateContext();

    if (isFirstPartyOnly()) {
        dispatchPage(pageName, properties, context);

        return;
    }

    window.analytics?.page(null, pageName, properties, {
        context,
        integrations: integrationsWithOutIdsInEvents,
    });
};

export const identify = (traits?: Record<string, unknown>) => {
    debugTracking('-> V4 identify request:', !!window.analytics, traits);

    if (!shouldTrack()) {
        return;
    }

    const eventContext = generateContext();

    if (isFirstPartyOnly()) {
        dispatchIdentify(traits, eventContext.context);

        return;
    }

    window.analytics?.identify(getTrackingService().userId, traits, eventContext);
};

/**
 * The `context.page` block Segment's analytics.js sets from `window.location` on every call.
 *
 * The first-party dispatch has to include it too: the metrics ingest
 * (`perspective-metrics`, `poc/schemas.baseActivitySchema`) requires `context.page.search`
 * and rejects the whole activity without it — which is why a first-party event returned 200
 * but never produced a metrics row. `context.page.search` is also where that service parses
 * UTM attribution from. On the consented path Segment overwrites this with the same values.
 */
const pageContext = () => ({
    path: window.location.pathname,
    referrer: document.referrer,
    search: window.location.search,
    title: document.title,
    url: window.location.href,
});

const generateContext = () => {
    const pipeline = getTrackingService().getEventContext();

    return {
        context: {
            perspective: omit(pipeline, 'trackingTitles'),
            dictionary: pipeline.trackingTitles,
            page: pageContext(),
            userAgent: window.navigator.userAgent,
            locale: window.navigator.language,
        },
    };
};

export default {
    track,
    page,
    identify,
};
